import { Decoder, string, succeed } from '@mojotech/json-type-validation'
import { dequal } from 'dequal'
import { format } from 'date-fns'
import memoizeOne from 'memoize-one'
import * as React from 'react'
import { GUID, parseGuid } from './guids'
import { err, ok, Result } from './result'
import {
  AlertLevel,
  AlertMessage,
  AlertTableSortSettings,
  ClientExposuresTransactions,
  ClientRiskData,
  DashboardModel,
  HubConnected,
  HubState,
  LimitType,
  AlertLevels,
  AlertLevelsDict,
  MutedClientMetricsDict,
  MutedMetrics,
  RawDashboardResponse,
  Severity,
  SortType,
  Toast,
  ToastsList,
  User, HasCode,
} from './types/types'
import {ClientCode, ClientConfig} from "./types/clients";

export const extractClientDataMemoized = memoizeOne(
  extractClientData,
  dequal,
)


export function parseDashboardData(
  response: RawDashboardResponse,
  currentTime: Date,
): DashboardModel {
  return {
    clients: extractClientDataMemoized(response.data),
    lastRJEMessageTimestamp: response.latestMessageTimestamp,
    lastUpdated: currentTime,
    totalMessagesCount: response.totalMessageCount,
    dailyTotalNewOrdersValue: response.dailyTotalNewOrderValue,
    dailyExecutedOrdersValue: response.dailyExecutedOrderValue,
  }
}

function translateRawItem(
  code: ClientCode,
  raw: ClientRiskData,
): ClientExposuresTransactions {
  return {
    code,
    rc2exposureInYen: raw.rc2.value,
    rc2exposureLimitInYen: raw.rc2.breachLimit,
    rc2exposurePercent: raw.rc2.percentValue,
    rc6unactionedOrders: raw.rc6.value,
    rc6unactionedOrdersLimit: raw.rc6.breachLimit,
    rc6unactionedOrdersPercent: raw.rc6.percentValue,
    // We'll presumably want to show RC7 later, not worth removing it for now?
    rc7messagesPerSecond: 1,
    rc7messagesPerSecondPercent: 1,
    revExposureOrdersValue: raw.rev.value,
    revExposureOrdersLimit: raw.rev.breachLimit,
    revExposureOrdersPercent: raw.rev.percentValue,
    maxRevExposureOrdersValue: raw.maxRev.value,
    maxRevExposureOrdersPercent: raw.maxRev.percentValue,
    historicMaxRevExposureValue: raw.historicMaxRev.value,
    historicMaxRevExposurePercent: raw.historicMaxRev.percentValue,
  }
}

const translateRawItemMemoized = memoizeOne(
  translateRawItem,
  dequal,
)

function extractClientData(
  responseData: RawDashboardResponse['data'],
): ReadonlyArray<ClientExposuresTransactions> {
  // SignalR / C# mangles the casing of object keys during serialization, so
  // it looks like we have to either force symbols to all-uppercase or deal
  // with C#-style ProperCase key names.
  // See also: https://github.com/aspnet/SignalR/issues/1415
  const clientSymbols: ReadonlyArray<string> = Object.keys(responseData).map(
    symbol => symbol.toUpperCase(),
  )
  const allValues: ReadonlyArray<ClientRiskData> = Object.keys(
    responseData,
  ).map((key: string) => responseData[key])

  return clientSymbols.map(function assemble(
    code: string,
    index: number,
  ): ClientExposuresTransactions {
    return translateRawItemMemoized(code, allValues[index])
  })
}

export function getRCForLimitType(
  limitType: LimitType,
): 'RC1' | 'RC2' | 'RC6' | 'RC7' | 'VirtualServerSuspension' | 'REV' | 'MaxREV' | 'HistoricMaxRev'{
  switch (limitType) {
    case "Virtual_server_suspension":
      return 'VirtualServerSuspension'
    case 'RC1_total_clip_size':
      return 'RC1'
    case 'RC2_exposure':
      return 'RC2'
    case 'RC6_unactioned_orders':
      return 'RC6'
    case 'RC7_messages_per_second':
      return 'RC7'
    case 'Realistic_exposure_value':
      return 'REV'
    case 'Historic_Max_Realistic_exposure':
      return 'HistoricMaxRev'
    default:
      return assertNever(limitType)
  }
}

export function assertNever(_: never): never {
  throw new Error('TypeScript let this slip through...')
}

/**
 *
 * @param unit 100 = '100%'.
 */
export function unitToPercentString(unit: number): string {
  return unit.toLocaleString() + '%'
}

// The following Set operations are from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set
// Also available from there (or show git history for this line): isSuperset, union, intersection, symmetricDifference

/**
 * Return the items that exist in setA but not setB.
 */
export function difference<T>(
  setA: Iterable<T>,
  setB: Iterable<T>,
): ReadonlySet<T> {
  const resultSet = new Set(setA)
  for (const elem of setB) {
    resultSet.delete(elem)
  }
  return resultSet
}

export function userCountsAsLoggedIn(user: User): boolean {
  switch (user.type) {
    case 'LoggedIn':
    case 'FailedToLogout':
      return true
    case 'LoggingIn':
    case 'NotLoggedIn':
    case 'FailedToLogIn':
      return false
    default:
      return assertNever(user)
  }
}

/**
 * If the `target` is undefined ("Nothing"), then every sample matches.
 * Used so we can "turn off" a search.
 */
export function containsIfNotUndefined(
  target: string | undefined,
): (sample: string) => boolean {
  return function _equalityIfNotUndefined(sample: string): boolean {
    if (target === undefined) {
      return true
    }
    return sample.toLowerCase().includes(target.toLowerCase())
  }
}

/**
 * Use this in places that are loosely typed, as an indication that the value
 * is expected to be of a certain type.
 *
 * E.g., a library function takes an `any` argument and passes it back to
 * you as another `any` type later, but you want to be clear that at both
 * points it should be the same, known type.
 *
 * For example usage, see `AxisName` in `Gatekeeper/ClientApp/src/components/ExposureChart.tsx`.
 *
 * @param x a concrete instance of that type.
 */
export function only<T>(x: T): T {
  return x
}

/**
 * TODO: Move these "obfuscated" symbols to the server / db, where ABN AMRO can edit them.
 * Symbols available in gatekeeper_noto_emojis.ttf
 * U+1F435
 * U+1F98D
 * U+1F436
 * U+1F43A
 * U+1F98A
 * U+1F431
 * U+1F981
 * U+1F42F
 * U+1F434
 * U+1F984
 * U+1F993
 * U+1F98C
 * U+1F42E
 * U+1F437
 * U+1F992
 * U+1F418
 * U+1F98F
 * U+1F42D
 * U+1F439
 * U+1F430
 * U+1F43B
 * U+1F428
 * U+1F43C
 * U+1F983
 * U+1F414
 * U+1F413
 * U+1F426
 * U+1F427
 * U+1F54A
 * U+1F985
 * U+1F986
 * U+1F989
 * U+1F438
 * U+1F40D
 * U+1F40B
 * U+1F42C
 * U+1F420
 * U+1F421
 * U+1F988
 * U+1F419
 * U+1F980
 * U+1F990
 * U+1F991
 * U+1F40C
 * U+1F98B
 * U+1F41B
 * U+1F41D
 * U+1F41E
 * U+1F997
 * U+1F577
 */
const symbols: ReadonlyArray<string> = [
  '🐵',
  '🦍',
  '🐶',
  '🐺',
  '🦊',
  '🐱',
  '🦁',
  '🐯',
  '🐴',
  '🦄',
  '🦓',
  '🦌',
  '🐮',
  '🐷',
  '🦒',
  '🐘',
  '🦏',
  '🐭',
  '🐹',
  '🐰',
  '🐻',
  '🐨',
  '🐼',
  '🦃',
  '🐔',
  '🐓',
  '🐦',
  '🐧',
  '🕊',
  '🦅',
  '🦆',
  '🦉',
  '🐸',
  '🐍',
  '🐋',
  '🐬',
  '🐠',
  '🐡',
  '🦈',
  '🐙',
  '🦀',
  '🦐',
  '🦑',
  '🐌',
  '🦋',
  '🐛',
  '🐝',
  '🐞',
  '🦗',
  '🕷',
]

export function obfuscateSymbol(i: number): string {
  return symbols[i % symbols.length]
}

export const guidDecoder = string().andThen(
  (guidString: string): Decoder<GUID> => {
    const parseResult = parseGuid(guidString)
    if (parseResult.ok) {
      return succeed(parseResult.result)
    }
    return Decoder.fail<GUID>(parseResult.error)
  },
)

/**
 * For sorting lists of the ClientState union type.
 *
 * E.g.: const sortedClients = [...clients].sort(byClientCode)
 *
 * (You don't need to use the array spread syntax, but be warned
 * that .sort will mutate the original array!)
 */
export function byClientCode(
  clientA: HasCode,
  clientB: HasCode,
): number {
  if (clientA.code < clientB.code) {
    return -1
  }
  if (clientA.code > clientB.code) {
    return 1
  }
  return 0
}

export function bySequenceNumberDescending(
  a: AlertMessage,
  b: AlertMessage,
): number {
  return b.sequenceNumber - a.sequenceNumber
}

export const defaultMetricLevels: AlertLevels = {
  rc1: 'Baseline',
  rc2: 'Baseline',
  rc6: 'Baseline',
  rc7: 'Baseline',
  virtualServerSuspension: 'Baseline',
  rev: 'Baseline',
  maxRev: 'Baseline',
  historicMaxRev: 'Baseline'
}

/**
 * Parses alert data into a dict, keyed off the clientCode (e.g, `AKUN`.)
 *
 * Works on the assumption that alerts are always passed oldest-first.
 *
 * TODO: handle "stale" alerts (by not updating from them?)
 *
 * @param oldLevels
 * @param alert
 */
export function updateMetricLevels(
  oldLevels: AlertLevelsDict,
  alert: AlertMessage,
): AlertLevelsDict {
  const key = alert.companyCode
  const levelsToUpdate = oldLevels[key] || defaultMetricLevels
  const newLevel = getNewLevel(
    alert.alertSeverity,
    alert.exceeded,
    alert.limitType,
  )

  switch (alert.limitType) {
    case "Virtual_server_suspension":
      return (updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        virtualServerSuspension: newLevel
      }))
    case 'RC1_total_clip_size':
      return updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        rc1: newLevel,
      })
    case 'RC2_exposure':
      return updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        rc2: newLevel,
      })
    case 'RC6_unactioned_orders':
      return updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        rc6: newLevel,
      })
    case 'RC7_messages_per_second':
      return updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        rc7: newLevel,
      })
    case 'Realistic_exposure_value':
      return updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        rev: newLevel,
      })
    case 'Historic_Max_Realistic_exposure':
      return updateRecord(oldLevels, key, {
        ...levelsToUpdate,
        historicMaxRev: newLevel,
      })

    default:
      return assertNever(alert.limitType)
  }
}

export function getNewLevel(
  severity: Severity,
  isExceeded: boolean,
  limitType: LimitType,
): AlertLevel {
  const isRevLimit = limitType === 'Realistic_exposure_value'

  switch (severity) {
    case 'Breach':
      return isExceeded ? 'Breach' : 'Warn'
    case 'Warn':
      return isExceeded ? 'Warn' : isRevLimit ? 'Baseline' : 'Notify'
    case 'Notify':
      return isExceeded ? 'Notify' : 'Baseline'
    default:
      return assertNever(severity)
  }
}

export function getOldLevel(
  severity: Severity,
  isExceeded: boolean,
  limitType: LimitType,
): AlertLevel {
  return getNewLevel(severity, !isExceeded, limitType)
}

/**
 * Immutably update a Record (object) with a dynamic key.
 *
 * This was done because you can't use the spread operator on
 * records in Typescript 3.1
 * This seems nasty, but may not need to always be that way:
 * https://stackoverflow.com/a/53188216/2014893
 */
export function updateRecord<K extends string, V>(
  record: Record<K, V>,
  key: K,
  value: V,
): Record<K, V> {
  const newObject = { ...(record as object) } as Record<K, V>
  // tslint:disable-next-line:no-object-mutation
  newObject[key] = value
  return newObject
}

export function generateKeyFrom(a: AlertMessage): string {
  return [
    a.sequenceNumber,
    format(a.alertTimestamp, 'T'),
    a.companyCode,
    a.limitType,
    'exceed=' + a.exceeded,
    a.alertSeverity,
    a.rjeMessageNumber,
    format(a.rjeMessageTimestamp, 'T'),
  ].join('__')
}

export function highestLevelInMetrics(
  mutedMetrics: MutedMetrics,
  metrics: AlertLevels,
): AlertLevel {
  // No `Object.values()` means adding properties to MetricLevels doesn't
  // cause a TypeError here :(
  const base: ReadonlyArray<[AlertLevel, 'muted' | 'not_muted']> = [
    [metrics.rc1, mutedMetrics.RC1_total_clip_size],
    [metrics.rc2, mutedMetrics.RC2_exposure],
    [metrics.rc6, mutedMetrics.RC6_unactioned_orders],
    [metrics.rc7, mutedMetrics.RC7_messages_per_second],
    [metrics.virtualServerSuspension, mutedMetrics.Virtual_server_suspension],
    [metrics.rev, mutedMetrics.Realistic_exposure_value],
    [metrics.historicMaxRev, mutedMetrics.Historic_Max_Realistic_exposure]
  ]

  const unmutedLevels: ReadonlyArray<AlertLevel> = base
    .filter(
      ([_, state]: [AlertLevel, 'muted' | 'not_muted']) =>
        state === 'not_muted',
    )
    .map(([level, _]) => level)

  return unmutedLevels.reduce(highestLevel, 'Baseline')
}

export function getHighestLevel(
  dict: AlertLevelsDict,
  mutedClientMetrics: MutedClientMetricsDict,
  clientCode: ClientCode,
): AlertLevel {
  const levels: AlertLevels | undefined = dict[clientCode]
  if (!levels) {
    return 'Baseline'
  }

  const mutedMetrics = mutedClientMetrics[clientCode] || defaultMetricMutes

  // No `Object.values()` :(
  return highestLevelInMetrics(mutedMetrics, levels)
}

export function highestLevelFromAll(
  guidLookup: Record<ClientCode, GUID | undefined>,
  mutedClientMetrics: MutedClientMetricsDict,
  dict: AlertLevelsDict,
): AlertLevel {
  return Object.keys(dict)
    .map(clientCode => {
      const mutedMetrics = mutedClientMetrics[clientCode] || defaultMetricMutes

      // Assumption: the MetricLevelsDict will never store `undefined` under a key.
      // (will `Object.values()` avoid this issue?)
      return highestLevelInMetrics(mutedMetrics, dict[
        clientCode
      ] as AlertLevels)
    })
    .reduce(highestLevel, 'Baseline')
}

export function highestLevel(a: AlertLevel, b: AlertLevel): AlertLevel {
  switch (a) {
    case 'Baseline':
      return b
    case 'Breach':
      return a
    case 'Notify':
      switch (b) {
        case 'Baseline':
        case 'Notify':
          return a
        case 'Warn':
        case 'Breach':
          return b
        default:
          return assertNever(b)
      }
    case 'Warn':
      switch (b) {
        case 'Baseline':
        case 'Notify':
        case 'Warn':
          return a
        case 'Breach':
          return b
        default:
          return assertNever(b)
      }
    default:
      return assertNever(a)
  }
}

/**
 * Promise-based way to add delays.
 * Creates a new Promise.
 *
 * Example:
 *
 *     delay(500)
 *       .then(doSomething)
 *
 */
export function delay<T>(milliseconds: number): Promise<T> {
  const stuff = (resolve: (someValue: T) => void): void => {
    setTimeout(resolve, milliseconds)
  }
  return new Promise(stuff)
}

/**
 * Take an array like `[1,2,4,5,15,14]`, return the differences: `[1,2,1,10,-1]`.
 */
export function toDeltas(source: ReadonlyArray<number>): ReadonlyArray<number> {
  return source.slice(1).map((next, index) => next - source[index])
}

/**
 * Take an array like `[4,4,4,0,0,3]`, return the run lengths:
 *    `[[3, 4], [2, 0], [1, 3]]`.
 *
 * Example:
 *
 *    [4,4,4,0,0,3].reduce(runLengthEncode, [])
 */
export function runLengthEncode(
  accumulator: ReadonlyArray<[number, number]>,
  next: number,
): ReadonlyArray<[number, number]> {
  if (accumulator.length === 0) {
    return [[1, next]]
  }
  const [prevCount, previousVal]: [number, number] = accumulator.slice(-1)[0]
  if (next === previousVal) {
    return [...accumulator.slice(0, -1), [prevCount + 1, next]]
  }
  return [...accumulator, [1, next]]
}

export function updateSortBy(
  oldSettings: AlertTableSortSettings,
  field: keyof AlertMessage,
  sortType: SortType,
): AlertTableSortSettings {
  const settingsSansThisColumn = oldSettings.filter(([col, _]) => col !== field)

  if (sortType === 'none') {
    return settingsSansThisColumn
  }

  return [...settingsSansThisColumn, [field, sortType]]
}

export function alertLevelToComparable(level: AlertLevel | Severity): number {
  switch (level) {
    case 'Breach':
      return 3
    case 'Warn':
      return 2
    case 'Notify':
      return 1
    case 'Baseline':
      return 0
    default:
      return assertNever(level)
  }
}

export function limitTypeToComparable(metric: LimitType): number {
  switch (metric) {
    case 'RC1_total_clip_size':
      return 0
    case 'RC2_exposure':
      return 1
    case 'RC6_unactioned_orders':
      return 2
    case 'RC7_messages_per_second':
      return 3
    case "Virtual_server_suspension":
      return 4
    case "Realistic_exposure_value":
      return 5
    case "Historic_Max_Realistic_exposure":
      return 6
    default:
      return assertNever(metric)
  }
}

/**
 * Returns exactly one AlertMessage for each combination of ClientCode & LimitType.
 *
 * If the alerts are passed in most-recent-first order, it has the effect of
 * returning only "active" alerts; that is, the alert representing the most
 * current state of the metric for each client.
 */
export function getOneAlertPerClientAndMetric(
  alerts: ReadonlyArray<AlertMessage>,
): ReadonlyArray<AlertMessage> {
  type Observation = Record<LimitType, boolean>
  type Observed = Record<ClientCode, Observation | undefined>
  type Accumulator = [ReadonlyArray<AlertMessage>, Observed]
  const defaultObservation: Observation = {
    RC1_total_clip_size: false,
    RC2_exposure: false,
    RC6_unactioned_orders: false,
    RC7_messages_per_second: false,
    Virtual_server_suspension: false,
    Realistic_exposure_value: false,
    Historic_Max_Realistic_exposure: false
  }

  function reducer(
    [alertsAcc, observedAcc]: Accumulator,
    nextAlert: AlertMessage,
  ): Accumulator {
    const observedForThisClient = observedAcc[nextAlert.companyCode]
    if (observedForThisClient === undefined) {
      return [
        [...alertsAcc, nextAlert],
        updateRecord(
          observedAcc,
          nextAlert.companyCode,
          updateRecord(defaultObservation, nextAlert.limitType, true),
        ),
      ]
    }
    if (observedForThisClient[nextAlert.limitType]) {
      // seen it; drop it.
      return [alertsAcc, observedAcc]
    }
    // Haven't seen this Client + Metric combo before; hang on to it.
    return [
      [...alertsAcc, nextAlert],
      updateRecord(
        observedAcc,
        nextAlert.companyCode,
        updateRecord(observedForThisClient, nextAlert.limitType, true),
      ),
    ]
  }

  const [actives, _]: Accumulator = alerts.reduce(reducer, [[], {}])

  return actives
}

export function getConnectionInfo(hub: HubState): Result<HubConnected, string> {
  switch (hub.state) {
    case 'RETRY_AFTER':
    case 'NOT_CONNECTED':
    case 'CONNECTING':
      return err('hub not ready: ' + hub.state)
    case 'CONNECTED':
      return ok(hub)
    default:
      return assertNever(hub)
  }
}

export function customAlertLevelBadge(
  classNames: string,
  level: AlertLevel,
  text: JSX.Element | string,
  onBadgeClick: 'no onClick' | ((level: AlertLevel) => () => void),
): JSX.Element {
  const onClick =
    onBadgeClick === 'no onClick'
      ? (): void => {
        /* void */
      }
      : onBadgeClick(level)

  return (
    <span
      className={`${classNames} alert-level-badge alert-level-badge--${level.toLowerCase()}`}
      onClick={onClick}
      title={`Severity: ${level}`}
    >
      <span className='alert-level-badge__text'>{text}</span>
    </span>
  )
}

export const defaultMetricMutes: MutedMetrics = {
  RC1_total_clip_size: 'not_muted',
  RC2_exposure: 'not_muted',
  RC6_unactioned_orders: 'not_muted',
  RC7_messages_per_second: 'not_muted',
  Virtual_server_suspension: 'not_muted',
  Realistic_exposure_value: 'not_muted',
  Historic_Max_Realistic_exposure: 'muted'
}

/**
 * Usage:
 *
 *    const lookup = listOfClients.reduce(toGuidLookupTable, {})
 *
 * where `listOfClients` is a `ReadonlyArray<Client>`
 */
export function toGuidLookupTable(
  accumulator: Record<ClientCode, GUID | undefined>,
  next: ClientConfig,
): Record<ClientCode, GUID | undefined> {
  return updateRecord(accumulator, next.name, next.id)
}

/**
 * Adds a single toast to the array, removing those with duplicate text and content.
 *
 * To add multiple toasts, use reduce (aka "fold"):
 *
 *    const allToasts = newToasts.reduce(addToast, oldToasts)
 *
 * @param toasts
 * @param newToast
 */
export function addToast(toasts: ToastsList, newToast: Toast): ToastsList {
  const existingToasts = toasts.filter(
    t =>
      (t.content === newToast.content && t.header === newToast.header) ||
      t.id === newToast.id,
  )

  if (existingToasts.length > 0) {
    return toasts
  }

  return [...toasts, newToast]
}

export function validateVirtualServerId(virtualServerId: string) {
  return virtualServerId.length === 6 && virtualServerId.match(/^[a-z0-9]+$/i);
}

export function validateCumulativeOrderValueLimitInJPY(cumulativeOrderValueLimitInJPY: string) {
  return cumulativeOrderValueLimitInJPY.length > 0  && cumulativeOrderValueLimitInJPY !== "0"
}
